"use client";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import without from "lodash/without";
import union from "lodash/union";
import castArray from "lodash/castArray";
import isObject from "lodash/isObject";
import forOwn from "lodash/forOwn";
import pickBy from "lodash/pickBy";
import reduce from "lodash/reduce";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import { filterActions } from "@stadium/redux-utils/slices";
import { FilterStateObj } from "@stadium/redux-utils/slices/filter.slice";
import { selectFilterState } from "@stadium/redux-utils/selectors/filterSelector";
import {
  DEFAULT_PER_PAGE,
  DEFAULT_CURRENT_PAGE,
  SystemFilterKeys,
  SYSTEM_KEYS,
} from "@stadium/redux-utils/constants/filter";

import {
  FilterType,
  FilterConfigItem,
  FilterConfigOption,
  FilterChangeArgs,
} from "@stadium/shared/src/components/filter/types";

// ─── Global Listener Optimization ───────────────────────────────────

/** Global set of active init functions across all useFilter hook instances. */
const activeInitCallbacks = new Set<() => void>();

/** Dispatches the sync logic across all hook instances. */
const handleGlobalPopState = () => {
  const iterator = activeInitCallbacks.values();
  const firstCallback = iterator.next().value;
  if (firstCallback) firstCallback();
};

// ─── Types ───────────────────────────────────────────────────────────

type RangeFilterValue = {
  min?: number | string;
  max?: number | string;
};

const DEFAULT_EXCLUDE_KEYS = [SystemFilterKeys.PER_PAGE, SystemFilterKeys.SORT];
const DEFAULT_ARR = [];

// ─── Shared Helpers ──────────────────────────────────────────────────

/** Checks if a filter value is non-empty (not null, undefined, "", [], or empty range object). */
const isNonEmpty = (v: unknown): boolean => {
  if (v == null || v === "") return false;
  if (Array.isArray(v)) return v.length > 0;
  if (isObject(v)) return Object.values(v).some((val) => val != null && val !== "");
  return true;
};

/** Converts a raw value to a Number, or returns a fallback (default: undefined) if it's null/empty. */
const toNumber = (v: string | number | null | undefined, fallback?: number): number | undefined =>
  v != null && v !== "" ? Number(v) : fallback;

/** Finds the parent option whose children contain the given value. */
const findParentOption = (config: FilterConfigItem[], id: string, valStr: string) => {
  const configItem = config.find((c) => c.id === id);
  const options = configItem?.options;
  if (!Array.isArray(options)) return undefined;

  return options.find((o) =>
    o.children?.some((child) => (child.value || child.id).toString() === valStr),
  );
};

/** Syncs parent selection based on whether any children are still selected. */
const syncParentSelection = (values: string[], parentOption: FilterConfigOption): string[] => {
  const parentValue = (parentOption.value || parentOption.id).toString();
  const childrenValues = new Set(
    (parentOption.children || []).map((c) => (c.value || c.id).toString()),
  );

  if (!values.some((v) => childrenValues.has(v))) {
    return without(values, parentValue);
  }
  return values.includes(parentValue) ? values : [...values, parentValue];
};

// ─── URL ↔ State Utilities ───────────────────────────────────────────

/** Parses URL search params into filter state. */
const syncFiltersFromUrl = (
  searchParams: URLSearchParams,
  validUrlSettings: Set<string>,
  newFilterState: FilterStateObj,
) => {
  searchParams.forEach((value, key) => {
    if (validUrlSettings.has(key)) return;

    if (key.startsWith("low_") || key.startsWith("high_")) {
      const filterId = key.replace(/^(low_|high_)/, "");
      if (newFilterState[filterId]) return;

      const low = searchParams.get(`low_${filterId}`);
      const high = searchParams.get(`high_${filterId}`);
      if (low !== null || high !== null) {
        newFilterState[filterId] = {
          min: toNumber(low, 0),
          max: toNumber(high, 0),
        };
      }
      return;
    }
    if (key.endsWith("[]")) {
      const filterId = key.replace("[]", "");
      newFilterState[filterId] = value ? value.split(",") : [];
      return;
    }
    newFilterState[key] = value;
  });
};

/** Applies pagination and sort from query params to filter state. */
const applyPaginationAndSort = (query: Record<string, any>, newFilterState: FilterStateObj) => {
  if (!newFilterState[SystemFilterKeys.SORT]) {
    const sort = query[SystemFilterKeys.SORT];
    if (sort) newFilterState[SystemFilterKeys.SORT] = sort;
  }

  if (
    newFilterState[SystemFilterKeys.PAGE] === undefined ||
    newFilterState[SystemFilterKeys.PAGE] === DEFAULT_CURRENT_PAGE
  ) {
    const page = query[SystemFilterKeys.PAGE] || query.page || DEFAULT_CURRENT_PAGE;
    newFilterState[SystemFilterKeys.PAGE] = Number(page);
  }

  if (
    newFilterState[SystemFilterKeys.PER_PAGE] === undefined ||
    newFilterState[SystemFilterKeys.PER_PAGE] === DEFAULT_PER_PAGE
  ) {
    const perPage = query[SystemFilterKeys.PER_PAGE] || query.per_page || DEFAULT_PER_PAGE;
    newFilterState[SystemFilterKeys.PER_PAGE] = Number(perPage);
  }
};

/** Normalizes range filter keys (low_/high_) into { min, max } objects. */
const normalizeRangeFilter = (
  newQuery: Record<string, any>,
  currentFilterState: FilterStateObj,
  normalizedQuery: Partial<FilterStateObj>,
) => {
  Object.keys(newQuery).forEach((key) => {
    const baseKey = key.replace(/^(low_|high_)/, "");
    if (normalizedQuery.hasOwnProperty(baseKey)) return;

    const lowVal = newQuery[`low_${baseKey}`];
    const highVal = newQuery[`high_${baseKey}`];

    if (lowVal === "" && highVal === "") {
      normalizedQuery[baseKey] = undefined;
      return;
    }

    const low = lowVal ?? currentFilterState[baseKey]?.min;
    const high = highVal ?? currentFilterState[baseKey]?.max;

    if (low !== undefined || high !== undefined) {
      const min = toNumber(low);
      const max = toNumber(high);
      normalizedQuery[baseKey] = min !== undefined || max !== undefined ? { min, max } : undefined;
    }
  });
};

/** Converts Redux filter state into URL search params. */
const mapStateToParams = (state: FilterStateObj, params: URLSearchParams) => {
  forOwn(pickBy(state, isNonEmpty), (value, key) => {
    switch (key) {
      case SystemFilterKeys.PAGE:
        if (Number(value) > 1) params.set(SystemFilterKeys.PAGE, value.toString());
        return;
      case SystemFilterKeys.SORT:
        params.set(SystemFilterKeys.SORT, value.toString());
        return;
      case SystemFilterKeys.PER_PAGE:
        if (Number(value) !== DEFAULT_PER_PAGE)
          params.set(SystemFilterKeys.PER_PAGE, value.toString());
        return;
      case SystemFilterKeys.SEARCH:
        params.set(SystemFilterKeys.SEARCH, value.toString());
        return;
    }

    if (Array.isArray(value)) {
      params.set(`${key}[]`, value.join(","));
    } else if (isObject(value)) {
      const rangeValue = value as RangeFilterValue;
      if (rangeValue.min != null && rangeValue.min !== "") {
        params.set(`low_${key}`, rangeValue.min.toString());
      }
      if (rangeValue.max != null && rangeValue.max !== "") {
        params.set(`high_${key}`, rangeValue.max.toString());
      }
    } else {
      params.set(key, value.toString());
    }
  });
};

// ─── Hook ────────────────────────────────────────────────────────────

export const useFilter = (config: FilterConfigItem[] = DEFAULT_ARR) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const currentFilterState = useSelector(selectFilterState);

  const filterStateRef = useRef<FilterStateObj>(currentFilterState);
  const routerRef = useRef(router);

  // Keep ref in sync with Redux for initial loads or external updates
  filterStateRef.current = currentFilterState;
  routerRef.current = router;

  const pathname = routerRef.current.asPath.split("?")[0];

  // ─── URL Sync ────────────────────────────────────────────────────

  const pushStateToUrl = (state: FilterStateObj) => {
    const url = new URL(routerRef.current.asPath, window.location.origin);
    const params = new URLSearchParams(url.search);

    // Clear all existing non-system parameters. We re-add them from state in mapStateToParams.
    Array.from(params.keys()).forEach((key) => {
      if (!SYSTEM_KEYS.has(key)) {
        params.delete(key);
      }
    });

    mapStateToParams(state, params);

    const searchString = params.toString();
    const nextUrl = searchString ? `${url.pathname}?${searchString}` : url.pathname;

    if (nextUrl !== routerRef.current.asPath) {
      routerRef.current.push(nextUrl, { scroll: false });
    }
  };

  // Create a ref for the push function to keep the debounce stable

  /**
   * Debounced URL push that skips execution if navigation occurred since it was triggered.
   * This prevents stale pushes (e.g., from cleanups) from reverting the URL.
   */
  const debouncedPushStateToUrl = useMemo(
    () =>
      debounce((capturedPath?: string) => {
        // Skip if we navigated away since the debounce was scheduled
        if (capturedPath && capturedPath !== routerRef.current.asPath) return;
        pushStateToUrl(filterStateRef.current);
      }, 500),
    [],
  );

  // ─── State Update ────────────────────────────────────────────────

  const updateFilterState = useCallback(
    (type: FilterType | SystemFilterKeys, newQuery: Record<string, any>) => {
      const normalizedQuery: Partial<FilterStateObj> = {};

      if (type === FilterType.RANGE) {
        normalizeRangeFilter(newQuery, filterStateRef.current, normalizedQuery);
      } else {
        Object.assign(normalizedQuery, newQuery);
      }

      if (type !== SystemFilterKeys.PAGE) {
        normalizedQuery[SystemFilterKeys.PAGE] = 1;
      }

      if (!isEmpty(normalizedQuery)) {
        // Update Ref immediately so debounced push has latest data
        filterStateRef.current = { ...filterStateRef.current, ...normalizedQuery };
        dispatch(filterActions.updateFilterState(normalizedQuery));
        debouncedPushStateToUrl(routerRef.current.asPath);
      }
    },
    [dispatch, debouncedPushStateToUrl], // removed asPath to pass it to debounce
  );

  const init = useCallback(() => {
    debouncedPushStateToUrl.cancel();
    const newFilterState: FilterStateObj = {};

    // Get the latest URL from the window directly, as routerRef might be stale during popstate
    const searchParams = new URLSearchParams(window.location.search);

    const syncFilterStr = searchParams.get("syncFilter");
    if (syncFilterStr) {
      searchParams.delete("syncFilter");
      try {
        const syncFilterObj = JSON.parse(syncFilterStr);
        Object.assign(newFilterState, syncFilterObj);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("useFilter: Error parsing syncFilter", e);
      }
    }

    // Combine dynamic params from routerRef with current search params.
    // We filter out stale SYSTEM_KEYS from the router query to avoid leaking values
    // that should be reset if the new URL lacks them.
    const query = {
      ...reduce(
        routerRef.current.query,
        (acc: Record<string, any>, value, key) => {
          if (!SYSTEM_KEYS.has(key)) acc[key] = value;
          return acc;
        },
        {},
      ),
      ...Object.fromEntries(searchParams.entries()),
    };

    syncFiltersFromUrl(searchParams, SYSTEM_KEYS, newFilterState);
    applyPaginationAndSort(query, newFilterState);

    filterStateRef.current = newFilterState;
    dispatch(filterActions.setFilterState(newFilterState));

    if (syncFilterStr) {
      searchParams.delete("syncFilter");
      const nextUrl =
        window.location.pathname + (searchParams.toString() ? `?${searchParams.toString()}` : "");
      routerRef.current.replace(nextUrl, { scroll: false });
    }
  }, [dispatch, debouncedPushStateToUrl]);

  // Cleanup for the debounce on unmount
  useEffect(() => {
    return () => {
      debouncedPushStateToUrl.cancel();
    };
  }, [debouncedPushStateToUrl, pathname]);

  // Sync Redux with URL when user navigates through browser history (Back/Forward)
  useEffect(() => {
    activeInitCallbacks.add(init);

    if (activeInitCallbacks.size === 1) {
      window.addEventListener("popstate", handleGlobalPopState);
    }

    return () => {
      activeInitCallbacks.delete(init);
      if (activeInitCallbacks.size === 0) {
        window.removeEventListener("popstate", handleGlobalPopState);
      }
    };
  }, [init]);

  // Sync state when URL changes (primarily for syncFilter and same-page nav)
  useEffect(() => {
    // Only the 'leader' hook (first one in the Set) should handle the URL sync
    // to avoid multiple redundant updates and race conditions.
    const isLeader = activeInitCallbacks.values().next().value === init;
    if (!isLeader) return;

    const url = new URL(router.asPath, window.location.origin);
    if (url.searchParams.has("syncFilter")) {
      init();
    }
  }, [router.asPath, init]);

  // ─── Filter Helpers ──────────────────────────────────────────────

  const handleMultiSelectChange = useCallback(
    (id: string, newValue: any, checked: boolean, config?: FilterConfigItem[]) => {
      const currentState = filterStateRef.current[id];
      const currentValues = castArray(currentState || []).map((v) => v.toString());
      const valStr =
        typeof newValue === "string" || typeof newValue === "number" ? newValue.toString() : "";

      let updatedValues = checked ? union(currentValues, [valStr]) : without(currentValues, valStr);

      const parentOption = config && findParentOption(config, id, valStr);
      if (parentOption) updatedValues = syncParentSelection(updatedValues, parentOption);

      updateFilterState(FilterType.MULTI_SELECT, { [id]: updatedValues });
    },
    [updateFilterState],
  );

  const handleSingleSelectChange = useCallback(
    (id: string, newValue: any, config?: FilterConfigItem[]) => {
      const currentState = filterStateRef.current[id];
      const isDeselect = currentState === newValue;
      const valStr =
        typeof newValue === "string" || typeof newValue === "number" ? newValue.toString() : "";

      let finalValue: string | string[] = isDeselect ? "" : valStr;

      if (!isDeselect && config && valStr) {
        const configItem = config.find((c) => c.id === id);
        const options = configItem?.options;
        if (Array.isArray(options)) {
          const option = options.find((o) => (o.value || o.id).toString() === valStr);
          if (option?.children?.length) {
            const childrenValues = option.children.map((c) => (c.value || c.id).toString());
            finalValue = [valStr, ...childrenValues];
          }
        }
      }

      updateFilterState(FilterType.SINGLE_SELECT, { [id]: finalValue });
    },
    [updateFilterState],
  );

  const handleRangeChange = useCallback(
    (id: string, newValue: any) => {
      if (isObject(newValue) && !Array.isArray(newValue)) {
        updateFilterState(FilterType.RANGE, {
          [`low_${id}`]: (newValue as RangeFilterValue).min ?? "",
          [`high_${id}`]: (newValue as RangeFilterValue).max ?? "",
        });
      }
    },
    [updateFilterState],
  );

  // ─── Filter Actions ──────────────────────────────────────────────

  const clear = useCallback(
    (excludeKeys: string[] = DEFAULT_EXCLUDE_KEYS) => {
      debouncedPushStateToUrl.cancel();

      const newState = reduce(
        filterStateRef.current,
        (acc: FilterStateObj, value, key) => {
          if (excludeKeys.includes(key)) {
            acc[key] = value;
          }
          return acc;
        },
        {} as FilterStateObj,
      );

      filterStateRef.current = newState;
      dispatch(filterActions.setFilterState(newState));

      const currentPathname = window.location.pathname;
      if (currentPathname === pathname) {
        debouncedPushStateToUrl(routerRef.current.asPath);
      }
    },
    [dispatch, debouncedPushStateToUrl, pathname],
  );

  const onChange = useCallback(
    (
      type: FilterType | SystemFilterKeys,
      { id, value: newValue, checked, config, _e }: FilterChangeArgs,
    ) => {
      _e?.stopPropagation();

      switch (type) {
        case FilterType.MULTI_SELECT:
          handleMultiSelectChange(id, newValue, !!checked, config);
          break;
        case FilterType.SINGLE_SELECT:
          handleSingleSelectChange(id, newValue, config);
          break;
        case FilterType.RANGE:
          handleRangeChange(id, newValue);
          break;
        default:
          updateFilterState(type, { [id]: newValue });
          break;
      }
    },
    [handleMultiSelectChange, handleSingleSelectChange, handleRangeChange, updateFilterState],
  );

  /** Removes a specific filter value (for multiselect). */
  const remove = useCallback(
    ({
      id,
      value,
      type,
    }: {
      id: string;
      value?: string | number | RangeFilterValue;
      type: FilterType;
    }) => {
      if (type === FilterType.RANGE) {
        updateFilterState(FilterType.RANGE, {
          [`low_${id}`]: "",
          [`high_${id}`]: "",
        });
        return;
      }

      const currentState = filterStateRef.current[id];
      if (value === undefined || currentState == null) return;

      const currentValues = castArray(currentState).map((v) => (isObject(v) ? "" : v.toString()));
      const valStr = isObject(value) ? "" : value.toString();
      let newValues = currentValues.filter((v) => v !== valStr);

      if (config && valStr) {
        const parentOption = findParentOption(config, id, valStr);
        if (parentOption) {
          const parentValue = (parentOption.value || parentOption.id).toString();
          newValues = newValues.filter((v) => v !== parentValue);
        }
      }

      updateFilterState(FilterType.MULTI_SELECT, { [id]: newValues });
    },
    [updateFilterState, config],
  );

  /** Active filters excluding system keys (sort, pagination). */
  const appliedFilters = useMemo(
    () =>
      pickBy(filterStateRef.current, (value, key) => !SYSTEM_KEYS.has(key) && isNonEmpty(value)),
    [currentFilterState],
  );

  return useMemo(
    () => ({ onChange, clear, remove, appliedFilters, data: filterStateRef.current, init }),
    [onChange, clear, remove, appliedFilters, currentFilterState, init],
  );
};
